import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Box,
  Modal,
  TextField,
  InputAdornment,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  FileText,
  Users,
  Books,
  Lightning,
  BuildingOffice,
  Microphone,
  SpeakerLow,
  Folders,
  CheckSquare,
  PlusCircle,
  Files
} from '@phosphor-icons/react';
import { useAppUser } from '@/contexts/AppUserProvider';

interface SearchPopoverProps {
  open: boolean;
  onClose: () => void;
  onNavigate?: (path: string) => void;
}

interface SearchResult {
  id: string;
  title: string;
  subtitle?: string;
  icon: React.ReactNode;
  path: string;
  type: 'page' | 'template' | 'person' | 'record';
}

// All navigation results - will be filtered based on user permissions
const allNavigationResults: SearchResult[] = [
  {
    id: '1',
    title: 'Record',
    subtitle: 'Start a new recording session',
    icon: <Microphone size={20} />,
    path: '/create/record',
    type: 'page'
  },
  {
    id: '2',
    title: 'Upload Files',
    subtitle: 'Upload files to create a session',
    icon: <PlusCircle size={20} />,
    path: '/create/upload',
    type: 'page'
  },
  {
    id: '3',
    title: 'Templates',
    subtitle: 'Manage document templates',
    icon: <FileText size={20} />,
    path: '/organization?tab=templates',
    type: 'page'
  },
  {
    id: '4',
    title: 'People',
    subtitle: 'Manage contacts and speakers',
    icon: <Users size={20} />,
    path: '/people',
    type: 'page'
  },
  {
    id: '5',
    title: 'Folders',
    subtitle: 'Group and organize files',
    icon: <Folders size={20} />,
    path: '/collections',
    type: 'page'
  },
  {
    id: '6',
    title: 'Files',
    subtitle: 'View and manage files',
    icon: <Files size={20} />,
    path: '/records',
    type: 'page'
  },
  {
    id: '6',
    title: 'Tasks',
    subtitle: 'View and manage tasks',
    icon: <CheckSquare size={20} />,
    path: '/tasks',
    type: 'page'
  },
  {
    id: '7',
    title: 'Ask',
    subtitle: 'Chat with AI assistant',
    icon: <Lightning size={20} weight="light" />,
    path: '/ai-chat',
    type: 'page'
  },
  {
    id: '8',
    title: 'Organization',
    subtitle: 'Company settings and management',
    icon: <BuildingOffice size={20} />,
    path: '/organization',
    type: 'page'
  }
];

export const SearchPopover: React.FC<SearchPopoverProps> = ({
  open,
  onClose,
  onNavigate
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const inputRef = useRef<HTMLInputElement>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const { appUser } = useAppUser();

  // Filter navigation results based on user permissions
  // Memoize to prevent infinite loop in useEffect
  const navigationResults = useMemo(() => {
    return allNavigationResults.filter((item) => {
      // Hide Tasks and People for non-super admins (Ask is now available to all)
      if (
        !appUser?.isSuperAdmin &&
        (item.path === '/tasks' || item.path === '/people')
      ) {
        return false;
      }
      return true;
    });
  }, [appUser?.isSuperAdmin]);

  const [filteredNavigationResults, setFilteredNavigationResults] =
    useState<SearchResult[]>(navigationResults);
  const [selectedIndex, setSelectedIndex] = useState<number>(-1);

  // Filter results based on search query
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredNavigationResults(navigationResults);
    } else {
      const query = searchQuery.toLowerCase();

      const filteredNav = navigationResults.filter(
        (result) =>
          result.title.toLowerCase().includes(query) ||
          result.subtitle?.toLowerCase().includes(query)
      );

      setFilteredNavigationResults(filteredNav);
    }
    // Reset selection when search changes
    setSelectedIndex(-1);
  }, [searchQuery, navigationResults]);

  // Clear search when popover closes and focus input when it opens
  useEffect(() => {
    if (!open) {
      setSearchQuery('');
      setSelectedIndex(-1);
    } else {
      // Focus the input when the modal opens
      const timer = setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [open]);

  const handleResultClick = (result: SearchResult) => {
    onNavigate?.(result.path);
    onClose();
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    const allResults = [...filteredNavigationResults];
    const maxIndex = allResults.length - 1;

    if (event.key === 'Escape') {
      onClose();
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      setSelectedIndex((prev) => (prev < maxIndex ? prev + 1 : 0));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setSelectedIndex((prev) => (prev > 0 ? prev - 1 : maxIndex));
    } else if (event.key === 'Enter') {
      if (selectedIndex >= 0 && allResults[selectedIndex]) {
        handleResultClick(allResults[selectedIndex]);
      } else {
        // If no selection, use first result
        const firstResult = filteredNavigationResults[0];
        if (firstResult) {
          handleResultClick(firstResult);
        }
      }
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
      BackdropProps={{
        sx: {
          backgroundColor: 'rgba(0, 0, 0, 0.5)'
        }
      }}
    >
      <Box
        sx={{
          width: isMobile ? '95vw' : '800px',
          maxWidth: isMobile ? '95vw' : '90vw',
          maxHeight: isMobile ? '85vh' : '67.5vh',
          borderRadius: isMobile ? '16px' : '10px',
          boxShadow: '0 25px 80px rgba(0, 0, 0, 0.15)',
          border: `1px solid ${theme.palette.divider}`,
          backgroundColor: theme.palette.background.paper,
          overflow: 'hidden',
          outline: 'none',
          display: 'flex',
          flexDirection: 'column',
          margin: isMobile ? '16px' : '0'
        }}
      >
        <Box sx={{ p: 0, display: 'flex', flexDirection: 'column', flex: 1 }}>
          {/* Header with Search Input */}
          <Box
            sx={{
              p: isMobile ? 1.5 : 2,
              borderBottom: `1px solid ${theme.palette.divider}`
            }}
          >
            <TextField
              fullWidth
              size="medium"
              placeholder="Search pages..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              inputRef={inputRef}
              variant="standard"
              slotProps={{
                input: {
                  disableUnderline: true
                }
              }}
              sx={{
                '& .MuiInput-root': {
                  fontSize: isMobile ? '18px' : '16px',
                  '&:before, &:after': {
                    display: 'none'
                  }
                },
                '& .MuiInputBase-input': {
                  padding: isMobile ? '8px 0' : '8px 0',
                  '&::placeholder': {
                    color: theme.palette.text.secondary,
                    opacity: 0.7
                  }
                }
              }}
            />
          </Box>

          {/* Search Results */}
          <Box
            sx={{
              flex: 1,
              overflowY: 'auto',
              minHeight: 0, // Important for flex children to scroll
              maxHeight: isMobile
                ? 'calc(85vh - 160px)'
                : 'calc(67.5vh - 120px)' // Subtract header and footer height
            }}
          >
            {filteredNavigationResults.length > 0 ? (
              <Box sx={{ p: isMobile ? 0.5 : 1 }}>
                {/* Navigation Section */}
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      px: 2,
                      py: 1,
                      color: theme.palette.text.secondary,
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                      fontSize: '11px'
                    }}
                  >
                    Navigation
                  </Typography>
                  <List sx={{ p: 0 }}>
                    {filteredNavigationResults.map((result, index) => {
                      const isSelected = selectedIndex === index;
                      return (
                        <ListItem key={result.id} disablePadding>
                          <ListItemButton
                            onClick={() => handleResultClick(result)}
                            sx={{
                              borderRadius: 1,
                              mb: 0.25,
                              py: isMobile ? 1 : 0.75,
                              px: isMobile ? 1.5 : 1.5,
                              backgroundColor: isSelected
                                ? theme.palette.action.selected
                                : 'transparent',
                              '&:hover': {
                                backgroundColor: isSelected
                                  ? theme.palette.action.selected
                                  : theme.palette.action.hover
                              }
                            }}
                          >
                            <ListItemIcon
                              sx={{
                                minWidth: 36,
                                color: theme.palette.text.secondary
                              }}
                            >
                              {result.icon}
                            </ListItemIcon>
                            <ListItemText
                              primary={
                                <Typography variant="body2" fontWeight={500}>
                                  {result.title}
                                </Typography>
                              }
                              secondary={
                                result.subtitle && (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    {result.subtitle}
                                  </Typography>
                                )
                              }
                            />
                          </ListItemButton>
                        </ListItem>
                      );
                    })}
                  </List>
                </Box>
              </Box>
            ) : (
              <Box
                sx={{
                  p: 6,
                  textAlign: 'center',
                  color: theme.palette.text.secondary,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%'
                }}
              >
                <Typography variant="h6" sx={{ mb: 1 }}>
                  {searchQuery
                    ? 'No results found'
                    : 'Start typing to search...'}
                </Typography>
                {!searchQuery && (
                  <Typography variant="body2" color="text.secondary">
                    Search for navigation pages
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};
